// ─── FormulasTab ──────────────────────────────────────────────────────────────
// Data Analytics → Formulas sub-tab — Excel-like spreadsheet.
//
//   - Toolbar: zoom + undo/redo + wrap + overflow.
//   - Name box (A1-style cell ref) + formula bar (function dropdown, cancel,
//     commit, fx) bound to the active cell.
//   - Spreadsheet of the real selected-parameter data. Data cells are
//     read-only; every other cell accepts formulas. react-spreadsheet ships
//     fast-formula-parser, so =SUM(B4:B10), =A4*2+B4, =AVERAGE(C4:C20),
//     =IF(B4>50, "high", "ok") etc. evaluate live as you type.
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useMemo, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import axios from 'axios'
import Spreadsheet, { createFormulaParser } from 'react-spreadsheet'
import { FormulaHelpers, Types, FormulaError } from 'fast-formula-parser'
import { ChevronDown, MoreVertical, Undo2, Redo2, X, Check, Loader, Library, FileDown, ArrowDownToLine, ArrowRightToLine, Calculator, FileText, FileSpreadsheet, Download, Printer } from 'lucide-react'
import { useVisualizeData } from './useVisualizeData'
import useAuthStore from '../../../store/auth-store'
import { resolveIanaTz } from '../../../lib/tz'

const API_URL       = import.meta.env.VITE_API_URL       || 'https://wqlive.ezxdemo.com'
const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'

// ─── Server-defined formula → BUILTIN_LIBRARY-shaped template ────────────────
// Given a row returned by GET /api/data/formulas/, produce an entry that
// plugs straight into the existing library dropdown + apply flow.
// Placeholders inside `expression`:
//   {key}       — numeric / select input value inserted literally
//   {key}[r]    — parameter input row reference (e.g. B4)
//   {key}.col   — parameter input column letter only (e.g. B)
// Binary-search a sorted [{x,y}] table and return the linearly-interpolated y
// for `x`. Values below the first x clamp to y[0]; values above the last x
// clamp to y[last]. Called once per row for lookup-type formulas.
function interpolateLookup(table, x) {
    if (!Array.isArray(table) || table.length === 0) return NaN
    if (x == null || Number.isNaN(x)) return NaN
    if (x <= table[0].x)                    return table[0].y
    if (x >= table[table.length - 1].x)     return table[table.length - 1].y
    // Binary search for the interval [i, i+1] containing x.
    let lo = 0, hi = table.length - 1
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1
        if (table[mid].x <= x) lo = mid; else hi = mid
    }
    const x1 = table[lo].x,  y1 = table[lo].y
    const x2 = table[hi].x,  y2 = table[hi].y
    if (x2 === x1) return y1
    return (x - x1) / (x2 - x1) * (y2 - y1) + y1
}

function serverFormulaToTemplate(f) {
    const isLookup = (f.formula_type || 'expression') === 'lookup'
    return {
        name:         f.name,
        output:       f.output_label || '',
        note:         f.description  || '',
        expression:   f.expression   || '',
        formulaType:  f.formula_type || 'expression',
        lookupData:   Array.isArray(f.lookup_data) ? f.lookup_data : [],
        // Normalize kinds so the modal's rendering code (which knows
        // 'singleParam' / 'number' / 'select') works unchanged.
        inputs: (f.inputs || []).map((i) => ({
            ...i,
            kind: i.kind === 'param' ? 'singleParam' : (i.kind || 'number'),
            defaultValue: i.default_value,
            options: i.options,
        })),
        build: ({ srcColsByKey, inputs, readCell }) => {
            const paramInputs = (f.inputs || []).filter(
                (x) => x.kind === 'param' || x.kind === 'singleParam',
            )
            const numericInputs = (f.inputs || []).filter(
                (x) => x.kind === 'number' || x.kind === 'select',
            )

            if (isLookup) {
                // Lookup-type: read the picked parameter's raw value at
                // row r, interpolate against the stored table, and
                // return the resulting number as a bare literal — no
                // Excel evaluation needed. We rely on `readCell` for
                // grid access (threaded in from applyLibrarySelection).
                const xInput = paramInputs[0]
                const table  = Array.isArray(f.lookup_data) ? f.lookup_data : []
                const xCol   = xInput ? (srcColsByKey?.[xInput.key] || 'B') : 'B'
                const rowValue = (r) => {
                    const raw = typeof readCell === 'function' ? readCell(r, xCol) : undefined
                    const x = raw == null || raw === '' ? NaN : Number(raw)
                    if (Number.isNaN(x)) return ''
                    const y = interpolateLookup(table, x)
                    return Number.isFinite(y) ? y : ''
                }
                // formulaFor returns a plain literal (not `=…`) so the
                // grid cell shows the interpolated value directly.
                return { kind: 'perRow', formulaFor: (r) => rowValue(r) }
            }

            // Expression path (unchanged).
            const expr = f.expression || ''
            // Two-pass substitution — the {key}[r] and {key}.col patterns
            // must be replaced BEFORE the bare {key} pattern, otherwise the
            // greedy match would consume "{key}" and leave "[r]" behind.
            const template = (r) => {
                let out = expr
                for (const p of paramInputs) {
                    const col = srcColsByKey?.[p.key] || 'B'
                    out = out.split(`{${p.key}}[r]`).join(`${col}${r}`)
                    out = out.split(`{${p.key}}.col`).join(col)
                }
                for (const p of numericInputs) {
                    let v = inputs?.[p.key]
                    if (v === undefined || v === null || v === '') v = p.default_value ?? 0
                    out = out.split(`{${p.key}}`).join(String(v))
                }
                return out.startsWith('=') ? out : `=${out}`
            }
            return { kind: 'perRow', formulaFor: template }
        },
    }
}

// Excel-compatible function set, grouped by category for the picker.
// fast-formula-parser (shipped with react-spreadsheet) already supports
// every entry below — this is just the curated list surfaced in the
// formula-bar dropdown. Categories follow Excel's "Insert Function"
// taxonomy so the picker reads naturally for spreadsheet-literate users.
const FUNCTION_CATEGORIES = [
    { label: 'Math & Trig', items: [
        'SUM','SUMIF','SUMIFS','SUMPRODUCT','PRODUCT','SUBTOTAL','AGGREGATE',
        'ABS','SIGN','SQRT','SQRTPI','POWER','EXP','LN','LOG','LOG10',
        'INT','MOD','QUOTIENT','GCD','LCM','TRUNC','FACT','FACTDOUBLE','COMBIN','PERMUT',
        'ROUND','ROUNDUP','ROUNDDOWN','MROUND','CEILING','FLOOR','EVEN','ODD',
        'PI','RAND','RANDBETWEEN','RADIANS','DEGREES',
        'SIN','COS','TAN','ASIN','ACOS','ATAN','ATAN2','SINH','COSH','TANH',
    ]},
    { label: 'Statistical', items: [
        'AVERAGE','AVERAGEA','AVERAGEIF','AVERAGEIFS','MEDIAN',
        'COUNT','COUNTA','COUNTIF','COUNTIFS','COUNTBLANK',
        'MIN','MINA','MINIFS','MAX','MAXA','MAXIFS',
        'STDEV','STDEVA','STDEV.S','STDEV.P','VAR','VARA','VAR.S','VAR.P',
        'LARGE','SMALL','RANK','PERCENTILE','QUARTILE','AVEDEV',
    ]},
    { label: 'Logical', items: [
        'IF','IFS','IFERROR','IFNA','AND','OR','NOT','XOR','TRUE','FALSE','SWITCH',
    ]},
    { label: 'Text', items: [
        'CONCAT','CONCATENATE','TEXTJOIN','LEFT','RIGHT','MID','LEN','TRIM','CLEAN',
        'UPPER','LOWER','PROPER','FIND','SEARCH','REPLACE','SUBSTITUTE','REPT',
        'EXACT','TEXT','VALUE','NUMBERVALUE','FIXED','DOLLAR','CHAR','CODE',
    ]},
    { label: 'Date & Time', items: [
        'NOW','TODAY','DATE','TIME','YEAR','MONTH','DAY','HOUR','MINUTE','SECOND',
        'WEEKDAY','WEEKNUM','ISOWEEKNUM','DATEDIF','DAYS','DAYS360','EDATE','EOMONTH',
        'NETWORKDAYS','WORKDAY','YEARFRAC','DATEVALUE','TIMEVALUE',
    ]},
    { label: 'Information', items: [
        'ISBLANK','ISNUMBER','ISTEXT','ISLOGICAL','ISERROR','ISERR','ISNA','ISEVEN','ISODD',
        'TYPE','NA','ERROR.TYPE','CELL','INFO',
    ]},
    { label: 'Units', items: [
        // Custom: convert a value between two units by name using the project's
        // Unitconversion factors, e.g. =CONVERT(B4, "F", "C").
        'CONVERT',
    ]},
]
// Flat list (still used by anything that just wants every supported name).
const FUNCTIONS = FUNCTION_CATEGORIES.flatMap((c) => c.items)
const COL_W = 150, ROW_H = 28, ROWHDR_W = 44, MIN_ROWS = 30, HEADER_ROWS = 3

function csvEscape(v) {
    const s = v == null ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

// ─── Formula Library — hydrology templates ───────────────────────────────────
// Each entry has typed inputs (rendered as a sub-form in the modal) and a
// `build` function that produces either a single aggregate formula or a
// per-row formula generator. Mirrors the legacy WQDataLIVE catalogue:
//
//   Manning Equation   — Q from level, channel shape, roughness, slope, unit
//   Stage Discharge    — Rating curve  Q = C × (h − h0)^n
//   TSS                — Linear calibration  TSS = m × turbidity + b
//
// All math is emitted as Excel-compatible expressions (POWER, SQRT, PI, …)
// so fast-formula-parser evaluates them client-side. Anything the user
// saves via "Save As Calculated Parameter" is appended in localStorage as
// a free-form `{ name, formula }` and rendered below the built-ins.
const BUILTIN_LIBRARY = [
    {
        name:   'Manning Equation',
        output: 'Discharge',
        note:   'Open-channel flow: Q = (k/n) · A · R^(2/3) · √S  (k = 1.49 US / 1.00 SI)',
        inputs: [
            { key: 'level', kind: 'singleParam', label: 'Level Parameter' },
            // Channel Shape names match the spec exactly so the diagram
            // lookup below can use the name as a key.
            { key: 'shape', kind: 'select',      label: 'Channel Shape',
              options: ['Rectangle', 'Trapezoid', 'Triangle', 'Trapezoid-Triangle', 'Circle'] },
            // channelDims is a composite input — the modal renders per-shape
            // dimension fields (D / H+W / B+H / B+T+H / B+T+H₁+H₂) and a
            // diagram of the chosen shape. Stored in `inputs.dims` as an
            // object keyed by dimension letter.
            { key: 'dims',  kind: 'channelDims', label: 'Channel Dimensions' },
            { key: 'n',     kind: 'number',      label: 'Roughness Coefficient (n)',
              defaultValue: 0.03, step: 'any', min: 0 },
            { key: 'slope', kind: 'number',      label: 'Channel Slope (S, m/m)',
              defaultValue: 0.001, step: 'any', min: 0 },
            { key: 'unit',  kind: 'select',      label: 'Unit',
              options: ['SI (m³/s)', 'English (cfs)'] },
        ],
        build: ({ srcCol, inputs }) => {
            const n     = Number(inputs.n)     || 0.03
            const slope = Number(inputs.slope) || 0.001
            // k = 1.49 in English (cfs, ft) — matches the spec; 1.00 in SI (m).
            const k     = inputs.unit === 'English (cfs)' ? 1.49 : 1
            const shape = inputs.shape || 'Rectangle'
            const dims  = inputs.dims  || {}
            const D     = Number(dims.D)  || 1
            const W     = Number(dims.W)  || 1
            const H     = Number(dims.H)  || 1
            const B     = Number(dims.B)  || 1
            const T     = Number(dims.T)  || 1
            const H1    = Number(dims.H1) || 1
            const H2    = Number(dims.H2) || 1
            const rootS = `POWER(${slope}, 0.5)`
            const kn    = `(${k}/${n})`

            const formulaFor = (r) => {
                const h = `${srcCol}${r}` // water depth at this row
                let A, R
                switch (shape) {
                    case 'Triangle': {
                        // Symmetric V, base B, height H. Side slope z = B/(2H);
                        // wetted slant length per side at depth h = h · sqrt(1 + z²).
                        // Top width at depth h = 2zh = B·h/H.
                        // A = (top_width_at_h · h)/2 = B·h²/(2H).
                        // P = 2·h·sqrt(1 + (B/(2H))²)
                        A = `(${B}*POWER(${h},2)/(2*${H}))`
                        R = `(${A}/(2*${h}*SQRT(1+POWER(${B}/(2*${H}),2))))`
                        break
                    }
                    case 'Trapezoid': {
                        // Bottom B, top T (at full height H). Side slope z = (T-B)/(2H);
                        // top width at depth h = B + 2zh = B + (T-B)·h/H.
                        // A = (B + zh)·h ; P = B + 2h·sqrt(1+z²)
                        const z = `((${T}-${B})/(2*${H}))`
                        A = `((${B}+${z}*${h})*${h})`
                        R = `(${A}/(${B}+2*${h}*SQRT(1+POWER(${z},2))))`
                        break
                    }
                    case 'Trapezoid-Triangle': {
                        // Lower triangle (bottom apex, top width B at the
                        // junction, height H₂) plus upper trapezoid (bottom
                        // B at the junction, top T at the brim, height H₁).
                        // If h ≤ H₂ → triangle-only fill.
                        //   A_tri(h) = B·h²/(2H₂); P_tri(h) = 2h·sqrt(1+(B/(2H₂))²)
                        // If h > H₂ → full triangle + trapezoid above with
                        // dt = h − H₂.
                        //   A_full_tri = (1/2)·B·H₂
                        //   side_full_tri = sqrt(H₂² + (B/2)²) × 2
                        //   z_trap = (T−B)/(2H₁)
                        //   A_trap(dt) = (B + z_trap·dt)·dt
                        //   side_trap(dt) = 2·dt·sqrt(1 + z_trap²)
                        const ATri = `(${B}*POWER(${h},2)/(2*${H2}))`
                        const PTri = `(2*${h}*SQRT(1+POWER(${B}/(2*${H2}),2)))`
                        const dt   = `(${h}-${H2})`
                        const zT   = `((${T}-${B})/(2*${H1}))`
                        const ATrap = `((${B}+${zT}*${dt})*${dt})`
                        const PTrap = `(2*${dt}*SQRT(1+POWER(${zT},2)))`
                        const APart = `((${B}*${H2})/2)`
                        const PPart = `(2*SQRT(POWER(${H2},2)+POWER(${B}/2,2)))`
                        A = `IF(${h}<=${H2}, ${ATri}, ${APart}+${ATrap})`
                        R = `(${A}/IF(${h}<=${H2}, ${PTri}, ${PPart}+${PTrap}))`
                        break
                    }
                    case 'Circle': {
                        // Partial flow in a circular pipe of diameter D.
                        // theta = 2·acos(1 − 2h/D); valid for 0 < h ≤ D.
                        const theta = `(2*ACOS(1-2*${h}/${D}))`
                        A = `(POWER(${D},2)/8*(${theta}-SIN(${theta})))`
                        R = `(${D}/4*(1-SIN(${theta})/${theta}))`
                        break
                    }
                    case 'Rectangle':
                    default: {
                        // Width W, water depth h. (Channel height H constrains
                        // the maximum h but doesn't enter the flow equation.)
                        A = `(${W}*${h})`
                        R = `(${A}/(${W}+2*${h}))`
                        break
                    }
                }
                return `=${kn}*${A}*POWER(${R}, 2/3)*${rootS}`
            }
            return { kind: 'perRow', formulaFor }
        },
    },
    {
        name:   'Stage Discharge',
        output: 'Discharge',
        note:   'Rating curve: Q = C · (h − h₀)^n',
        inputs: [
            { key: 'stage',  kind: 'singleParam', label: 'Stage Parameter' },
            { key: 'C',      kind: 'number', label: 'Coefficient (C)',      defaultValue: 1,    step: 'any' },
            { key: 'h0',     kind: 'number', label: 'Stage Offset (h₀)',     defaultValue: 0,    step: 'any' },
            { key: 'n',      kind: 'number', label: 'Exponent (n)',          defaultValue: 1.5,  step: 'any' },
        ],
        build: ({ srcCol, inputs }) => {
            const C  = Number(inputs.C)  || 0
            const h0 = Number(inputs.h0) || 0
            const n  = Number(inputs.n)  || 1
            return {
                kind:       'perRow',
                formulaFor: (r) => `=${C}*POWER(MAX(0,${srcCol}${r}-${h0}), ${n})`,
            }
        },
    },
    {
        name:   'TSS',
        output: 'Total Suspended Solids (mg/L)',
        note:   'Linear calibration from turbidity: TSS = m · turbidity + b',
        inputs: [
            { key: 'turbidity', kind: 'singleParam', label: 'Turbidity Parameter' },
            { key: 'm',         kind: 'number',      label: 'Slope (m)',     defaultValue: 1,   step: 'any' },
            { key: 'b',         kind: 'number',      label: 'Intercept (b)', defaultValue: 0,   step: 'any' },
        ],
        build: ({ srcCol, inputs }) => {
            const m = Number(inputs.m) || 0
            const b = Number(inputs.b) || 0
            return {
                kind:       'perRow',
                formulaFor: (r) => `=${m}*${srcCol}${r}${b >= 0 ? '+' : ''}${b}`,
            }
        },
    },
]
// ─── Channel shape catalogue (Manning Equation) ──────────────────────────────
// Each shape declares its dimension fields (matched to the spec letters) plus
// an inline SVG diagram drawn to a 200×140 viewBox so it slots into the
// Formula Library modal next to the dimension inputs. Water = light cyan
// fill; channel wall = dark cyan stroke; dashed line = water surface; "d"
// arrow shows the water level the sensor measures.
const CHANNEL_SHAPES = {
    Rectangle: {
        dims: [
            { key: 'H', label: 'H', hint: 'Channel Height' },
            { key: 'W', label: 'W', hint: 'Channel Width' },
        ],
        // Open rectangle: bottom + two vertical sides, water filling lower portion.
        svg: (
            <svg viewBox="0 0 200 140" className="w-full h-full">
                <rect x="20" y="20" width="160" height="100" fill="#cfeaf3" stroke="#2c6e8b" strokeWidth="1.5" />
                <line x1="20" y1="55" x2="180" y2="55" stroke="#2c6e8b" strokeDasharray="3 2" strokeWidth="1" />
                <text x="100" y="135" fontSize="10" textAnchor="middle" fill="#2c6e8b">W</text>
                <text x="185" y="75" fontSize="10" fill="#2c6e8b">H</text>
                <text x="32" y="85" fontSize="10" fill="#2c6e8b">d</text>
            </svg>
        ),
    },
    Trapezoid: {
        dims: [
            { key: 'B', label: 'B', hint: 'Bottom Width' },
            { key: 'T', label: 'T', hint: 'Top Width' },
            { key: 'H', label: 'H', hint: 'Channel Height' },
        ],
        svg: (
            <svg viewBox="0 0 200 140" className="w-full h-full">
                {/* Trapezoid: wider at top, narrower at bottom */}
                <polygon points="35,20 165,20 140,120 60,120" fill="#cfeaf3" stroke="#2c6e8b" strokeWidth="1.5" />
                <line x1="35" y1="55" x2="165" y2="55" stroke="#2c6e8b" strokeDasharray="3 2" strokeWidth="1" />
                <text x="100" y="14" fontSize="10" textAnchor="middle" fill="#2c6e8b">T</text>
                <text x="100" y="135" fontSize="10" textAnchor="middle" fill="#2c6e8b">B</text>
                <text x="170" y="75" fontSize="10" fill="#2c6e8b">H</text>
                <text x="48" y="85" fontSize="10" fill="#2c6e8b">d</text>
            </svg>
        ),
    },
    Triangle: {
        dims: [
            { key: 'B', label: 'B', hint: 'Base / Top Width' },
            { key: 'H', label: 'H', hint: 'Channel Height' },
        ],
        svg: (
            <svg viewBox="0 0 200 140" className="w-full h-full">
                {/* V-shape with apex at bottom */}
                <polygon points="30,20 170,20 100,120" fill="#cfeaf3" stroke="#2c6e8b" strokeWidth="1.5" />
                <line x1="30" y1="50" x2="170" y2="50" stroke="#2c6e8b" strokeDasharray="3 2" strokeWidth="1" />
                <text x="100" y="14" fontSize="10" textAnchor="middle" fill="#2c6e8b">B</text>
                <text x="108" y="75" fontSize="10" fill="#2c6e8b">H</text>
                <text x="100" y="115" fontSize="10" textAnchor="middle" fill="#2c6e8b">d</text>
            </svg>
        ),
    },
    'Trapezoid-Triangle': {
        dims: [
            { key: 'B',  label: 'B',  hint: 'Trapezoid Bottom / Triangle Top Width' },
            { key: 'T',  label: 'T',  hint: 'Trapezoid Top Width' },
            { key: 'H1', label: 'H₁', hint: 'Trapezoid Height' },
            { key: 'H2', label: 'H₂', hint: 'Triangle Height' },
        ],
        svg: (
            <svg viewBox="0 0 200 140" className="w-full h-full">
                {/* Trapezoid on top (B at bottom of trap, T at brim), V on bottom (apex). */}
                <polygon points="30,15 170,15 130,75 100,130 70,75" fill="#cfeaf3" stroke="#2c6e8b" strokeWidth="1.5" />
                <line x1="30" y1="45" x2="170" y2="45" stroke="#2c6e8b" strokeDasharray="3 2" strokeWidth="1" />
                <line x1="70" y1="75" x2="130" y2="75" stroke="#2c6e8b" strokeDasharray="2 2" strokeWidth="0.8" />
                <text x="100" y="11" fontSize="9" textAnchor="middle" fill="#2c6e8b">T</text>
                <text x="100" y="84" fontSize="9" textAnchor="middle" fill="#2c6e8b">B</text>
                <text x="135" y="50" fontSize="9" fill="#2c6e8b">H₁</text>
                <text x="105" y="108" fontSize="9" fill="#2c6e8b">H₂</text>
            </svg>
        ),
    },
    Circle: {
        dims: [
            { key: 'D', label: 'D', hint: 'Pipe Diameter' },
        ],
        svg: (
            <svg viewBox="0 0 200 140" className="w-full h-full">
                {/* Pipe interior; water fills lower portion. Mask the upper
                    half of the fill so it reads as partial flow. */}
                <defs>
                    <clipPath id="circleWaterMask">
                        <rect x="0" y="60" width="200" height="80" />
                    </clipPath>
                </defs>
                <circle cx="100" cy="70" r="55" fill="white" stroke="#2c6e8b" strokeWidth="1.5" />
                <circle cx="100" cy="70" r="55" fill="#cfeaf3" stroke="none" clipPath="url(#circleWaterMask)" />
                <line x1="45" y1="60" x2="155" y2="60" stroke="#2c6e8b" strokeDasharray="3 2" strokeWidth="1" />
                <text x="100" y="60" fontSize="10" textAnchor="middle" fill="#2c6e8b" dy="-2">D</text>
                <text x="100" y="115" fontSize="10" textAnchor="middle" fill="#2c6e8b">d</text>
            </svg>
        ),
    },
}

// ─── Calculated-parameter templates ───────────────────────────────────────────
// Higher-level "preset formulas" — each accepts typed inputs and emits the
// per-row or aggregate formula that gets applied to the destination column.
// Mirrors the legacy WQDataLIVE calculated-parameter catalogue:
//
//   Total Rain          — Σ over an interval-rain column
//   Flow Accumulation   — Σ over a flow column
//   Offset              — y = x + k
//   Rolling Average     — sliding mean over an N-second window
//   Parameter Average   — mean across N parameter columns at each timestamp
//   Math Formula        — free-form expression using P1, P2, … placeholders
//
// `inputs`  declares the schema the modal renders.
// `build({ inputs, srcCol, paramCols, rowOneBased, headerRows })`
//   returns either { kind: 'aggregate', formula }   — one cell at top of dest
//   or       { kind: 'perRow',   formulaFor(r) }    — function called per row
//
// `paramCols` is an array of column letters chosen by the user for the
// multi-parameter templates; `srcCol` is the single chosen column for the
// single-parameter templates.
const CALC_TEMPLATES = [
    {
        key:    'totalRain',
        name:   'Total Rain',
        note:   'Sum all data within the given period',
        inputs: [{ key: 'param', kind: 'singleParam', label: 'Interval Rain Parameter' }],
        build: ({ srcCol, headerRows, totalRows }) => ({
            kind:    'aggregate',
            formula: `=SUM(${srcCol}${headerRows + 1}:${srcCol}${totalRows})`,
        }),
    },
    {
        key:    'flowAccum',
        name:   'Flow Accumulation',
        note:   'Cumulative sum across the period',
        inputs: [{ key: 'param', kind: 'singleParam', label: 'Flow Parameter' }],
        build: ({ srcCol, headerRows, totalRows }) => ({
            kind:    'aggregate',
            formula: `=SUM(${srcCol}${headerRows + 1}:${srcCol}${totalRows})`,
        }),
    },
    {
        key:    'offset',
        name:   'Offset',
        note:   'Add a fixed offset (signed float) to a parameter',
        inputs: [
            { key: 'param',  kind: 'singleParam', label: 'Input Parameter' },
            { key: 'offset', kind: 'number',      label: 'Fixed Offset',     defaultValue: 0, step: 'any' },
        ],
        build: ({ srcCol, inputs }) => ({
            kind:        'perRow',
            formulaFor:  (r) => `=${srcCol}${r}${Number(inputs.offset) >= 0 ? '+' : ''}${Number(inputs.offset) || 0}`,
        }),
    },
    {
        key:    'rollingAvg',
        name:   'Rolling Average',
        note:   'Sliding window — mean of the last N seconds at each row',
        inputs: [
            { key: 'param',  kind: 'singleParam', label: 'Input Parameter' },
            { key: 'period', kind: 'number',      label: 'Average Period (seconds)', defaultValue: 3600, step: 60, min: 1 },
        ],
        // We don't know the device sample rate up-front, so the caller passes
        // an estimated `sampleSeconds` (median delta between consecutive
        // timestamps). Window-in-rows = max(1, round(period / sampleSeconds)).
        build: ({ srcCol, inputs, sampleSeconds, headerRows }) => {
            const period = Math.max(1, Number(inputs.period) || 0)
            const win    = Math.max(1, Math.round(period / Math.max(1, sampleSeconds || 60)))
            return {
                kind:       'perRow',
                formulaFor: (r) => {
                    const lo = Math.max(headerRows + 1, r - win + 1)
                    return `=AVERAGE(${srcCol}${lo}:${srcCol}${r})`
                },
            }
        },
    },
    {
        key:    'paramAvg',
        name:   'Parameter Average',
        note:   'Mean across multiple parameter columns at each timestamp',
        inputs: [{ key: 'params', kind: 'multiParam', label: 'Parameters', min: 2 }],
        build: ({ paramCols }) => ({
            kind:        'perRow',
            formulaFor:  (r) => `=AVERAGE(${paramCols.map((c) => `${c}${r}`).join(',')})`,
        }),
    },
    {
        key:    'mathFormula',
        name:   'Math Formula',
        note:   'Free-form math expression using P1, P2, … placeholders (e.g. (P1+P2)/2)',
        inputs: [
            { key: 'params',     kind: 'multiParam', label: 'Parameters', min: 1 },
            { key: 'expression', kind: 'text',       label: 'Expression', placeholder: '(P1 + P2) / 2', defaultValue: 'P1' },
        ],
        build: ({ paramCols, inputs }) => {
            const expr = String(inputs.expression || 'P1')
            return {
                kind:       'perRow',
                formulaFor: (r) => {
                    // Replace P1..Pn placeholders with the chosen column-letter+row
                    // references, longest-first so P10 doesn't get partially matched
                    // by P1.
                    let out = expr
                    for (let i = paramCols.length; i >= 1; i--) {
                        const re = new RegExp(`\\bP${i}\\b`, 'g')
                        out = out.replace(re, `${paramCols[i - 1]}${r}`)
                    }
                    return out.startsWith('=') ? out : `=${out}`
                },
            }
        },
    },
]

const LIBRARY_KEY = (userId, projectId) => `wq_formula_library_${userId || 'anon'}_${projectId || 'na'}`
const colLabel = (i) => { let s = ''; i += 1; while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26) } return s }
// A1-style label for {row,column} (both zero-indexed in react-spreadsheet's points).
const cellName = (pt) => pt ? `${colLabel(pt.column)}${pt.row + 1}` : ''

// Convert a column letter ('A','AA') to a zero-based column index. Inverse of colLabel.
const colIndex = (letter) => {
    const s = String(letter || '').toUpperCase()
    let n = 0
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i) - 64
        if (c < 1 || c > 26) return -1
        n = n * 26 + c
    }
    return n - 1
}

// Wrap a generated formula in ROUND(..., 2) so the displayed value is
// always 2 decimal places. Idempotent: if the formula is already
// `=ROUND(..., n)` we leave it alone so a user-supplied precision wins.
const round2 = (formula) => {
    const s = String(formula || '')
    if (!s.startsWith('=')) return formula
    if (/^=ROUND\s*\(/i.test(s)) return formula
    return `=ROUND(${s.slice(1)}, 2)`
}

// Shift every RELATIVE cell reference in a formula by (dr, dc). Mirrors
// Excel's fill behaviour: `A1` becomes `A2` when filled down one row;
// `B$5` keeps row 5 but columns shift; `$A$1` never moves.
//
// Regex captures: optional $ before column, the column letters, optional $
// before row, the row digits. Quoted strings inside a formula are skipped
// — Excel cell-refs don't appear inside quotes.
const shiftFormula = (formula, dr, dc) => {
    if (!formula || !formula.startsWith('=')) return formula
    // Split on double-quoted strings so we don't rewrite refs inside text literals.
    const parts = formula.split(/("[^"]*")/g)
    return parts.map((p, i) => {
        if (i % 2 === 1) return p // odd-indexed parts are quoted strings
        return p.replace(/(\$?)([A-Z]+)(\$?)(\d+)/g, (m, dCol, col, dRow, row) => {
            let newCol = col
            let newRow = row
            if (dCol !== '$') {
                const idx = colIndex(col) + dc
                if (idx < 0) return m // out of bounds — leave untouched
                newCol = colLabel(idx)
            }
            if (dRow !== '$') {
                const nr = Number(row) + dr
                if (nr < 1) return m
                newRow = String(nr)
            }
            return `${dCol}${newCol}${dRow}${newRow}`
        })
    }).join('')
}

export default function FormulasTab({ selectedParameters = [], dateRangeGraph, activePeriod, externalRefreshNonce = 0, mobileMenuSlot = null }) {
    const userId = useAuthStore((s) => s.user?.id)
    const [zoom, setZoom] = useState('100%')
    // ⋮ overflow menu (export/print). Shown in the toolbar on desktop and
    // portalled into the tab-row group on mobile (matches the XD).
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef(null)
    const [fn, setFn] = useState('SUM')
    const [active, setActive] = useState({ row: 3, column: 1 })
    const [grid, setGrid] = useState([])
    // Currently-selected column (0-indexed) when the user clicks a column
    // header (A, B, C, …). null when no whole-column selection is active.
    // Drives the column-level apply-formula flow: Formula Library writes
    // the chosen formula into the column immediately to the right of this
    // one (per-row for cell-ref formulas, single-cell for aggregate ones).
    const [selectedColumn, setSelectedColumn] = useState(null)
    // Last range selection ({start, end} of {row, column}) — drives Fill Down /
    // Fill Right. We keep it as state so the toolbar buttons see the most
    // recent selection even after focus moves away from the grid.
    const [rangeSel, setRangeSel] = useState(null)

    // Formula library (built-in + admin-defined + user-saved).
    //   built-in       — the 3 hard-coded hydrology templates above
    //   admin-defined  — CustomFormula rows fetched from /api/data/formulas/
    //                    (managed via /admin/formulas/ in the admin panel)
    //   user-saved     — legacy per-user localStorage entries (kept for
    //                    the existing "Save As Calculated Parameter" flow)
    const libraryStorageKey = LIBRARY_KEY(userId)
    const [userLibrary, setUserLibrary] = useState(() => {
        try { return JSON.parse(localStorage.getItem(LIBRARY_KEY(userId)) || '[]') }
        catch { return [] }
    })
    const [serverLibrary, setServerLibrary] = useState([])
    useEffect(() => {
        // No auth header needed — endpoint is public read-only. If we
        // ever plan-gate certain formulas server-side, add the JWT
        // header here and the backend will filter accordingly.
        let cancelled = false
        axios.get(`${API_URL}${API_BASE_PATH}/data/formulas/`)
            .then((res) => {
                if (cancelled) return
                const rows = res.data?.formulas || []
                setServerLibrary(rows.map(serverFormulaToTemplate))
            })
            .catch(() => { if (!cancelled) setServerLibrary([]) })
        return () => { cancelled = true }
    }, [])
    // Only server-side active formulas + user-saved formulas. The built-in
    // hydrology templates are no longer surfaced — every formula must be
    // defined in the backend so the parameter mapping (parameter_lib_id) is
    // authoritative and the frontend can auto-match without prompting.
    const library = useMemo(
        () => [...serverLibrary, ...userLibrary],
        [serverLibrary, userLibrary],
    )
    const persistLibrary = (next) => {
        setUserLibrary(next)
        try { localStorage.setItem(libraryStorageKey, JSON.stringify(next)) } catch {}
    }

    // Modal state
    const [libraryOpen, setLibraryOpen] = useState(false)
    const [librarySel, setLibrarySel]   = useState('')
    // Per-formula input form values, keyed by the input's `key`. Reset on
    // template change so swapping formulas doesn't carry stale values.
    const [libraryInputs, setLibraryInputs] = useState({})
    const setLibraryInput = (key, value) =>
        setLibraryInputs((s) => ({ ...s, [key]: value }))
    // Look up the currently-selected template across BOTH built-ins AND
    // server-defined formulas so the modal's input form renders correctly
    // for admin-managed rows too.
    const currentLibTpl = useMemo(
        () => library.find((f) => f.name === librarySel && Array.isArray(f.inputs) && typeof f.build === 'function'),
        [library, librarySel]
    )
    const [saveOpen, setSaveOpen]       = useState(false)
    const [saveOutputParam, setSaveOutputParam] = useState('')
    const [saveRename, setSaveRename]   = useState('')
    const [saveOutputCol, setSaveOutputCol]     = useState('C')

    // Use customer's configured tz so the Formulas grid pulls the same slice
    // as Visualize/Validate. Without this it falls back to the browser tz and
    // returns rows from a different point in the day.
    const rawCustomerTz = useAuthStore((s) => s.user?.timezone)
    const customerTz    = useMemo(() => resolveIanaTz(rawCustomerTz), [rawCustomerTz])
    const { data, loading } = useVisualizeData(selectedParameters, dateRangeGraph, activePeriod, externalRefreshNonce, customerTz)
    const zoomFactor = parseInt(zoom, 10) / 100
    const hasParams = selectedParameters.length > 0

    // ── ⋮ overflow menu: export the Formulas grid ──────────────────────────────
    useEffect(() => {
        if (!menuOpen) return
        const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false) }
        document.addEventListener('mousedown', close)
        return () => document.removeEventListener('mousedown', close)
    }, [menuOpen])
    const exportRows = () => grid.map((row) => (row || []).map((cell) => cell?.value ?? ''))
    const downloadCsv = () => {
        const csv = exportRows().map((row) => row.map(csvEscape).join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a'); a.href = url; a.download = 'formulas-data.csv'; a.click()
        URL.revokeObjectURL(url); setMenuOpen(false)
    }
    const downloadXls = async () => {
        const { utils, writeFile } = await import('xlsx')
        const ws = utils.aoa_to_sheet(exportRows())
        const wb = utils.book_new(); utils.book_append_sheet(wb, ws, 'Formulas')
        writeFile(wb, 'formulas-data.xlsx'); setMenuOpen(false)
    }
    const downloadPdf = async () => {
        const rows = exportRows()
        if (!rows.length) return
        const { jsPDF } = await import('jspdf')
        const { autoTable } = await import('jspdf-autotable')
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
        const pageW = doc.internal.pageSize.getWidth()
        const MARGIN = 36
        doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(20)
        doc.text('Data Analytics — Formulas', pageW / 2, MARGIN + 8, { align: 'center' })
        const head = rows.slice(0, HEADER_ROWS)
        const body = rows.slice(HEADER_ROWS)
        autoTable(doc, {
            startY: MARGIN + 32,
            margin: { left: MARGIN, right: MARGIN },
            styles: { fontSize: 8, cellPadding: 2.5, textColor: 40, overflow: 'linebreak' },
            headStyles: { fillColor: [245, 245, 245], textColor: 30, halign: 'center', fontStyle: 'bold' },
            columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
            theme: 'grid',
            head, body,
        })
        doc.save('formulas-data.pdf'); setMenuOpen(false)
    }
    const overflowMenu = (
        <div className="flex items-center gap-1 relative" ref={menuRef}>
            <button type="button" onClick={() => setMenuOpen((o) => !o)} className="p-1 text-gray-600 hover:bg-gray-100 rounded">
                <MoreVertical className="w-6 h-6" />
            </button>
            {menuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-30 w-56 py-1 text-sm">
                    <button onClick={downloadPdf} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                        <FileText className="w-4 h-4 text-gray-500 shrink-0" /> Download PDF
                    </button>
                    <button onClick={downloadCsv} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                        <Download className="w-4 h-4 text-gray-500 shrink-0" /> Download CSV
                    </button>
                    <button onClick={downloadXls} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                        <FileSpreadsheet className="w-4 h-4 text-gray-500 shrink-0" /> Download XLS
                    </button>
                    <button onClick={() => { window.print(); setMenuOpen(false) }} disabled={!hasParams} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                        <Printer className="w-4 h-4 text-gray-500 shrink-0" /> Print
                    </button>
                </div>
            )}
        </div>
    )

    const sheetRef = useRef(null)

    // ── CONVERT(value, "from", "to") — unit conversion inside formulas ────────
    // Load the project's unit-conversion factors (from/to unit name → slope,
    // intercept) once, keyed by "from\x00to" (lowercased). Held in a ref so the
    // formula parser reads the latest table at eval time without being recreated.
    const convRef = useRef(new Map())
    useEffect(() => {
        let cancelled = false
        axios.get(`${API_URL}${API_BASE_PATH}/data/unit-conversions/`)
            .then((res) => {
                if (cancelled) return
                const m = new Map()
                for (const c of (res.data?.conversions || [])) {
                    m.set(`${String(c.from).toLowerCase()} ${String(c.to).toLowerCase()}`, {
                        slope: Number(c.slope), intercept: Number(c.intercept),
                    })
                }
                convRef.current = m
            })
            .catch((e) => console.warn('[Formulas] unit-conversions load failed', e))
        return () => { cancelled = true }
    }, [])

    // Custom formula parser that adds CONVERT to react-spreadsheet's engine.
    // createFormulaParser(data, config) keeps its onCell/onRange and merges our
    // functions. The lookup reads convRef at call time.
    const makeFormulaParser = useMemo(() => (data) => createFormulaParser(data, {
        functions: {
            CONVERT: (value, from, to) => {
                const v = FormulaHelpers.accept(value, Types.NUMBER)
                const f = String(FormulaHelpers.accept(from, Types.STRING) || '').trim()
                const t = String(FormulaHelpers.accept(to, Types.STRING) || '').trim()
                if (!f || !t) throw FormulaError.VALUE
                if (f.toLowerCase() === t.toLowerCase()) return v
                const factor = convRef.current.get(`${f.toLowerCase()} ${t.toLowerCase()}`)
                if (!factor) throw FormulaError.NA   // #N/A — no conversion for this unit pair
                return v * factor.slope + factor.intercept
            },
        },
    }), [])

    const [box, setBox] = useState({ w: 0, h: 0 })
    useEffect(() => {
        const el = sheetRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver((e) => { const cr = e[0].contentRect; setBox({ w: cr.width, h: cr.height }) })
        ro.observe(el); return () => ro.disconnect()
    }, [])

    // Base matrix: data cells read-only, empty cells editable (for formulas).
    // Numeric data is stored as plain string ("12.34") so fast-formula-parser
    // can coerce it for SUM/AVG/etc.
    const baseMatrix = useMemo(() => {
        if (!data.length) return []
        const dataCols = data.reduce((m, r) => Math.max(m, r.length), 0)
        const fitCols = Math.max(0, Math.floor((box.w / zoomFactor - ROWHDR_W) / COL_W))
        const fitRows = Math.max(0, Math.floor((box.h / zoomFactor - ROW_H) / ROW_H))
        const totalCols = Math.max(dataCols, fitCols, 1)
        const totalRows = Math.max(data.length, fitRows, MIN_ROWS)
        const out = []
        for (let r = 0; r < totalRows; r++) {
            const src = data[r]
            const row = new Array(totalCols)
            for (let c = 0; c < totalCols; c++) {
                const cell = src && src[c]
                if (cell && (r < HEADER_ROWS || c === 0 || cell.value !== '')) row[c] = { value: cell.value, readOnly: true }
                else row[c] = { value: '' } // editable → formulas allowed
            }
            out.push(row)
        }
        return out
    }, [data, box.w, box.h, zoomFactor])

    // Reset the working grid whenever the underlying data/shape changes, but
    // keep a snapshot we can restore on "cancel".
    const savedRef = useRef(null)
    useEffect(() => { setGrid(baseMatrix) }, [baseMatrix])

    const activeRow  = grid[active.row]
    const activeCell = activeRow?.[active.column]
    const activeCellValue = activeCell?.value ?? ''
    const activeReadOnly = !!activeCell?.readOnly

    const setActiveCellValue = (raw) => {
        setGrid((g) => {
            const next = g.map((row) => row.slice())
            if (!next[active.row]) return g
            if (next[active.row][active.column]?.readOnly) return g
            next[active.row][active.column] = { ...next[active.row][active.column], value: raw }
            return next
        })
    }

    // Formula bar shows whatever's in the cell (formula or literal). When the
    // user pastes/types a leading "=", we don't add a second one.
    const barText = String(activeCellValue)
    const onBarChange = (text) => {
        // Allow plain literals too — only treat as a formula if it starts with "=".
        setActiveCellValue(text)
    }
    const insertFunction = (name) => {
        setFn(name)
        if (activeReadOnly) return
        // CONVERT needs unit args — scaffold a ready-to-edit template.
        if (name === 'CONVERT') {
            const current = String(activeCellValue || '')
            const val = current.startsWith('=') ? current.slice(1) : (current || '')
            setActiveCellValue(`=CONVERT(${val}, "F", "C")`)
            return
        }
        // If the bar is empty, scaffold "=NAME()"; otherwise wrap selection.
        const current = String(activeCellValue || '')
        if (!current) setActiveCellValue(`=${name}()`)
        else if (current.startsWith('=')) setActiveCellValue(`=${name}(${current.slice(1)})`)
        else setActiveCellValue(`=${name}(${current})`)
    }

    // Cancel (X) restores the last snapshot; Commit (✓) takes a fresh one.
    const cancelEdit = () => {
        if (savedRef.current) setGrid(savedRef.current)
    }
    const commitEdit = () => {
        savedRef.current = grid
    }

    const placeholder = activeReadOnly
        ? 'Read-only data cell — select any other cell to enter a formula'
        : 'Enter a value, or a formula starting with "=" (e.g. =SUM(B4:B10), =A4*2+B4, =AVERAGE(C4:C20))'

    // ── Calculated-parameter templates ──────────────────────────────────────
    // Each parameter the user has selected occupies a data column in the
    // grid, starting at column B (index 1). paramCols maps a parameter to
    // its column letter so the templates can build the right references.
    const paramColLetters = useMemo(
        () => selectedParameters.map((_, i) => colLabel(i + 1)),
        [selectedParameters]
    )
    // Rough sample-rate estimate (median delta between adjacent timestamps
    // in the first data column). Used by Rolling Average to turn a
    // period-in-seconds into a number of rows. Falls back to 600s (10 min)
    // if anything goes wrong — typical WQ device cadence.
    const sampleSeconds = useMemo(() => {
        const deltas = []
        for (let r = HEADER_ROWS; r + 1 < grid.length; r++) {
            const a = Date.parse(grid[r]?.[0]?.value)
            const b = Date.parse(grid[r + 1]?.[0]?.value)
            if (Number.isFinite(a) && Number.isFinite(b) && b > a) deltas.push((b - a) / 1000)
            if (deltas.length >= 25) break
        }
        if (!deltas.length) return 600
        deltas.sort((x, y) => x - y)
        return deltas[Math.floor(deltas.length / 2)]
    }, [grid])

    // Modal state for the calculated-parameter template picker.
    const [templateOpen, setTemplateOpen]       = useState(false)
    const [templateKey,  setTemplateKey]        = useState(CALC_TEMPLATES[0].key)
    // Per-template input values, keyed by the template's input key.
    // Starts blank and gets populated by the form below; reset on close.
    const [templateInputs, setTemplateInputs]   = useState({})
    const currentTemplate = useMemo(
        () => CALC_TEMPLATES.find((t) => t.key === templateKey) || CALC_TEMPLATES[0],
        [templateKey]
    )

    const openTemplates = () => {
        setTemplateKey(CALC_TEMPLATES[0].key)
        setTemplateInputs({})
        setTemplateOpen(true)
    }
    const setTplInput = (key, value) =>
        setTemplateInputs((s) => ({ ...s, [key]: value }))

    const applyTemplate = () => {
        const tpl = currentTemplate
        if (!tpl) return

        // Resolve parameter inputs → column letter(s). Single-param templates
        // expect `param` to be a parameter id; multi-param templates expect
        // `params` to be an array of ids.
        let srcCol = null, paramCols = []
        for (const input of tpl.inputs) {
            if (input.kind === 'singleParam') {
                const pid = templateInputs[input.key]
                const idx = selectedParameters.findIndex((p) => String(p.id) === String(pid))
                if (idx < 0) { alert(`Pick a parameter for "${input.label}".`); return }
                srcCol = paramColLetters[idx]
            } else if (input.kind === 'multiParam') {
                const ids = templateInputs[input.key] || []
                if (!Array.isArray(ids) || ids.length < (input.min || 1)) {
                    alert(`Pick at least ${input.min || 1} parameter(s) for "${input.label}".`); return
                }
                paramCols = ids.map((pid) =>
                    paramColLetters[selectedParameters.findIndex((p) => String(p.id) === String(pid))]
                ).filter(Boolean)
                if (paramCols.length !== ids.length) { alert('One of the chosen parameters could not be resolved.'); return }
            }
        }

        // Destination column: the column immediately to the right of the
        // primary source (single-param) or of the right-most chosen
        // parameter (multi-param). Mirrors the legacy "result lands next
        // to the input" convention.
        const baseLetter = srcCol || paramCols[paramCols.length - 1] || 'B'
        const destCol    = colIndex(baseLetter) + 1
        if (destCol < 1) return

        const built = tpl.build({
            srcCol:        srcCol,
            paramCols:     paramCols,
            inputs:        templateInputs,
            headerRows:    HEADER_ROWS,
            totalRows:     grid.length,
            sampleSeconds,
        })

        setGrid((g) => {
            const totalRows = g.length
            const next = g.map((row) => {
                const r = row.slice()
                // Insert a fresh cell at destCol, shifting existing
                // content (and any downstream parameter columns) one
                // position to the right. Avoids overwriting a sibling
                // parameter column with the new calculated column.
                while (r.length < destCol) r.push({ value: '' })
                r.splice(destCol, 0, { value: '' })
                return r
            })
            // Stamp the full 3-row header (Site / Device / Parameter) so the
            // generated column reads as a peer of the source columns. Row 1
            // takes the template's display name; row 2 is the same — the
            // Calc Templates spec doesn't separate "output name" from
            // "formula name" the way the Formula Library does.
            const stampHeader = (r, value) => {
                if (!next[r]) next[r] = []
                while (next[r].length <= destCol) next[r].push({ value: '' })
                next[r][destCol] = { value, readOnly: true }
            }
            stampHeader(0, 'Calculated')
            stampHeader(1, tpl.name)
            stampHeader(2, tpl.name)

            if (built.kind === 'aggregate') {
                next[HEADER_ROWS][destCol] = { value: round2(built.formula) }
            } else {
                for (let r = HEADER_ROWS; r < totalRows; r++) {
                    // Only emit for rows that have a value in the primary
                    // source column (skip empty/padding rows).
                    const probeCol = srcCol ? colIndex(srcCol) : colIndex(paramCols[0])
                    const probeVal = next[r]?.[probeCol]?.value
                    if (probeVal === '' || probeVal == null) continue
                    next[r][destCol] = { value: round2(built.formulaFor(r + 1)) }
                }
            }
            return next
        })
        setTemplateOpen(false)
    }

    // ── Excel-style Fill Down / Fill Right ──────────────────────────────────
    // Select a range starting from the cell that holds your formula, then
    // press Ctrl+D (down) / Ctrl+R (right) or click the toolbar buttons.
    // The top-most (or left-most) row's cells are propagated through the
    // selection with relative refs shifted; absolute refs ($) stay put.
    const _fill = (direction /* 'down' | 'right' */) => {
        const range = rangeSel
        if (!range || !range.start || !range.end) {
            alert('Select a range first — start with the cell containing your formula and extend the selection in the direction you want to fill.')
            return
        }
        const r0 = Math.min(range.start.row,    range.end.row)
        const r1 = Math.max(range.start.row,    range.end.row)
        const c0 = Math.min(range.start.column, range.end.column)
        const c1 = Math.max(range.start.column, range.end.column)
        if (direction === 'down' && r0 === r1) { alert('Extend the selection downward (more than one row) before Fill Down.'); return }
        if (direction === 'right' && c0 === c1) { alert('Extend the selection to the right (more than one column) before Fill Right.'); return }

        setGrid((g) => {
            const next = g.map((row) => row.slice())
            if (direction === 'down') {
                // For each selected column, take the top row's cell and
                // replicate it downward with row-only relative shift.
                for (let c = c0; c <= c1; c++) {
                    const src = next[r0]?.[c]
                    if (!src) continue
                    const raw = String(src.value ?? '')
                    if (raw === '') continue
                    for (let r = r0 + 1; r <= r1; r++) {
                        if (!next[r] || next[r][c]?.readOnly) continue
                        const shifted = raw.startsWith('=') ? shiftFormula(raw, r - r0, 0) : raw
                        next[r][c] = { ...next[r][c], value: shifted }
                    }
                }
            } else {
                // Fill Right: for each selected row, take the left-most
                // cell and replicate rightward with column-only relative shift.
                for (let r = r0; r <= r1; r++) {
                    const src = next[r]?.[c0]
                    if (!src) continue
                    const raw = String(src.value ?? '')
                    if (raw === '') continue
                    for (let c = c0 + 1; c <= c1; c++) {
                        if (!next[r] || next[r][c]?.readOnly) continue
                        const shifted = raw.startsWith('=') ? shiftFormula(raw, 0, c - c0) : raw
                        next[r][c] = { ...next[r][c], value: shifted }
                    }
                }
            }
            return next
        })
    }
    const fillDown  = () => _fill('down')
    const fillRight = () => _fill('right')

    // Ctrl+D / Ctrl+R while the Formulas tab is mounted — Excel's standard
    // bindings. preventDefault stops the browser bookmark / reload shortcuts
    // hijacking the key combo.
    useEffect(() => {
        const onKey = (e) => {
            if (!(e.ctrlKey || e.metaKey)) return
            if (e.key === 'd' || e.key === 'D') { e.preventDefault(); fillDown() }
            else if (e.key === 'r' || e.key === 'R') { e.preventDefault(); fillRight() }
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rangeSel])

    // ── Copy / paste with Excel-style relative-reference translation ─────────
    // react-spreadsheet's built-in paste copies the formula verbatim, so D4
    // "=B4+C4" pasted to D5 stays "=B4+C4". We intercept: record the copy
    // anchor (top-left of the selection at copy time), and on paste shift every
    // relative reference by the source→target offset (via shiftFormula, which
    // already honours $ absolute markers). Live refs avoid stale closures in
    // the native event listeners.
    const activeRef = useRef(null)
    const rangeRef = useRef(null)
    const copyAnchorRef = useRef(null)
    useEffect(() => { activeRef.current = active }, [active])
    useEffect(() => { rangeRef.current = rangeSel }, [rangeSel])
    useEffect(() => {
        const el = sheetRef.current
        if (!el) return
        const topLeft = () => {
            const r = rangeRef.current
            if (r?.start && r?.end) {
                return { row: Math.min(r.start.row, r.end.row), column: Math.min(r.start.column, r.end.column) }
            }
            return activeRef.current || null
        }
        const onCopyOrCut = () => { copyAnchorRef.current = topLeft() }
        const onPaste = (e) => {
            const src = copyAnchorRef.current
            if (!src) return  // no known copy anchor — let react-spreadsheet handle it

            // Target top-left AND span from the current selection, so pasting a
            // single copied cell into a highlighted RANGE fills the whole range
            // (Excel behaviour), not just the first cell.
            const r = rangeRef.current
            const act = activeRef.current
            let tgtTop, tgtLeft, spanRows, spanCols
            if (r?.start && r?.end) {
                tgtTop = Math.min(r.start.row, r.end.row)
                tgtLeft = Math.min(r.start.column, r.end.column)
                spanRows = Math.abs(r.end.row - r.start.row) + 1
                spanCols = Math.abs(r.end.column - r.start.column) + 1
            } else if (act) {
                tgtTop = act.row; tgtLeft = act.column; spanRows = 1; spanCols = 1
            } else return

            const text = e.clipboardData?.getData('text/plain')
            if (!text) return
            // Only take over when a formula is present, so plain number/text
            // pastes keep react-spreadsheet's native behaviour.
            if (!/(^|\t|\n)=/.test(text)) return

            // Parse the clipboard into a 2D block.
            const lines = text.replace(/\r/g, '').split('\n')
            if (lines.length && lines[lines.length - 1] === '') lines.pop()  // trailing newline
            const clip = lines.map((l) => l.split('\t'))
            const clipRows = clip.length || 1
            const clipCols = Math.max(1, ...clip.map((c) => c.length))

            // Fill at least the selection; tile the clipboard if the selection
            // is larger (single cell → whole range, or block → repeated).
            const outRows = Math.max(spanRows, clipRows)
            const outCols = Math.max(spanCols, clipCols)

            e.preventDefault()
            e.stopImmediatePropagation()
            setGrid((g) => {
                const next = g.map((row) => row.slice())
                for (let i = 0; i < outRows; i++) {
                    for (let j = 0; j < outCols; j++) {
                        const R = tgtTop + i
                        const C = tgtLeft + j
                        if (!next[R] || C >= next[R].length) continue
                        if (next[R][C]?.readOnly) continue        // never overwrite data cells
                        const cr = i % clipRows
                        const cc = j % clipCols
                        const cellText = (clip[cr] && clip[cr][cc] != null) ? clip[cr][cc] : ''
                        // Each destination shifts relative refs by its offset from
                        // the ORIGINAL source cell that produced this clip entry.
                        const dr = R - (src.row + cr)
                        const dc = C - (src.column + cc)
                        const val = cellText.startsWith('=') ? shiftFormula(cellText, dr, dc) : cellText
                        next[R][C] = { ...next[R][C], value: val }
                    }
                }
                return next
            })
        }
        el.addEventListener('copy', onCopyOrCut, true)
        el.addEventListener('cut', onCopyOrCut, true)
        el.addEventListener('paste', onPaste, true)
        return () => {
            el.removeEventListener('copy', onCopyOrCut, true)
            el.removeEventListener('cut', onCopyOrCut, true)
            el.removeEventListener('paste', onPaste, true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ── Formula Library ────────────────────────────────────────────────────
    //
    // Auto-match a formula's `singleParam` inputs to selectedParameters via
    // the backend-supplied `parameter_ref_id` (a Parameterlib id, derived
    // from Unitlib on the admin form). If exactly one matching column is
    // in the current selection, we bind it silently and the modal hides
    // the parameter picker. If none / multiple match, we leave the field
    // blank so the user can still resolve it manually.
    const autoMapInputs = (item) => {
        if (!item || !Array.isArray(item.inputs)) return {}
        const next = {}
        for (const inp of item.inputs) {
            if (inp.kind !== 'singleParam' && inp.kind !== 'param') continue
            const refId = inp.parameter_ref_id
            if (!refId) continue
            const matches = selectedParameters.filter(
                (sp) => String(sp.parameter_id) === String(refId),
            )
            if (matches.length === 1) next[inp.key] = matches[0].id
        }
        return next
    }
    const openLibrary = () => {
        // Open on the "Select Formula" step first (no formula pre-selected), per
        // the XD two-step flow: the Input fields + Output appear only after the
        // user picks a formula from the dropdown (see currentLibTpl gate below).
        setLibrarySel('')
        setLibraryInputs({})
        setLibraryOpen(true)
    }
    // Translate a library formula so it operates on `targetSrcCol` (a column
    // letter, e.g. 'D') instead of whatever the library entry was authored
    // against (typically 'B'). Every cell ref `LETTER+digits` whose letter
    // matches the formula's first-mentioned column is rewritten to use the
    // user's chosen column. Other refs are left alone so multi-column
    // formulas (e.g. =B5-B4 — both refs to B) get rewritten consistently.
    const _retargetFormula = (formula, targetSrcCol) => {
        const firstRef = formula.match(/([A-Z]+)\d+/)
        if (!firstRef) return formula
        const sourceLetter = firstRef[1]
        return formula.replace(/([A-Z]+)(\d+)/g, (m, col, row) =>
            col === sourceLetter ? `${targetSrcCol}${row}` : m
        )
    }

    // Per-row replication: rewrite every `LETTER+digits` so the digits
    // become `rowOneBased`, preserving the existing column letter.
    const _replicateRowNumbers = (formula, rowOneBased) =>
        formula.replace(/([A-Z]+)(\d+)/g, (m, col) => `${col}${rowOneBased}`)

    const applyLibrarySelection = () => {
        const item = library.find((f) => f.name === librarySel)
        if (!item) { setLibraryOpen(false); return }

        // Two entry shapes survive in `library`:
        //
        //   * Built-in templates (Manning / Stage Discharge / TSS) carry an
        //     `inputs` schema and a `build({ srcCol, inputs })` function.
        //     `srcCol` comes from the template's primary singleParam input.
        //   * User-saved entries (from "Save As Calculated Parameter") are
        //     flat `{ name, formula }` strings. We keep the old
        //     column-aware behaviour for those so previously-saved formulas
        //     still work after this redesign.
        const isTemplate = typeof item.build === 'function' && Array.isArray(item.inputs)

        if (isTemplate) {
            // Resolve every parameter input → column letter map. Server
            // formulas often reference multiple parameters, so we can't
            // rely on a single `primary` column any more. Built-ins that
            // only have one `singleParam` still work — srcCol below is
            // the first one, which is what they always used.
            const paramInputs = item.inputs.filter(
                (i) => i.kind === 'singleParam' || i.kind === 'param',
            )
            const srcColsByKey = {}
            for (const p of paramInputs) {
                const pid = libraryInputs[p.key]
                const idx = selectedParameters.findIndex((sp) => String(sp.id) === String(pid))
                if (idx < 0) {
                    alert(`Pick a parameter for "${p.label}".`); return
                }
                srcColsByKey[p.key] = colLabel(idx + 1)
            }
            const primary = paramInputs[0]
            const srcCol  = primary ? srcColsByKey[primary.key] : 'B'

            // Validate required selects/numbers — best-effort, leaves defaults alone.
            for (const i of item.inputs) {
                if (i.kind === 'select' && !libraryInputs[i.key] && !i.shapeOnly) {
                    // First option becomes the implicit default in the modal so
                    // an empty value here means the user never opened the dropdown.
                    libraryInputs[i.key] = i.options?.[0]
                }
            }

            // Lookup-type formulas need to read raw grid values per row
            // instead of emitting an Excel formula. We capture the grid
            // via setGrid's callback below, but the build fn is invoked
            // once and its per-row callback is called inside the loop —
            // so give it a readCell(r, colLetter) that closes over the
            // grid snapshot we're about to operate on.
            let gridSnapshot = null
            const readCell = (r, colLetter) => {
                const g = gridSnapshot
                if (!g) return undefined
                const c = colIndex(colLetter)
                return g[r - 1]?.[c]?.value
            }
            const built = item.build({ srcCol, srcColsByKey, inputs: libraryInputs, readCell })

            setGrid((g) => {
                gridSnapshot = g
                const totalRows = g.length
                // Append the calculated column at the first empty position
                // to the right of the selected-parameter columns, so we
                // never shift or overwrite existing data. If the previous
                // calculated column is still there, the next formula lands
                // beside it (right of the last non-empty header cell).
                const paramCount = selectedParameters.length
                const headerRow  = g[2] || []
                let destCol = Math.max(paramCount + 1, 1)
                for (let c = destCol; c < headerRow.length; c++) {
                    const v = headerRow[c]?.value
                    if (v !== '' && v != null) destCol = c + 1
                }

                const next = g.map((row) => {
                    const r = row.slice()
                    while (r.length <= destCol) r.push({ value: '' })
                    return r
                })
                // Header row 2 carries the backend output_label — that's the
                // column label the user sees. Rows 0/1 are cleared so the
                // calculated column reads as a standalone output rather than
                // borrowing headers from a source column.
                const stampHeader = (r, value) => {
                    if (!next[r]) next[r] = []
                    while (next[r].length <= destCol) next[r].push({ value: '' })
                    next[r][destCol] = { value, readOnly: true }
                }
                stampHeader(0, '')
                stampHeader(1, '')
                stampHeader(2, item.output || item.name)

                if (built.kind === 'aggregate') {
                    next[HEADER_ROWS][destCol] = { value: round2(built.formula) }
                } else {
                    for (let r = HEADER_ROWS; r < totalRows; r++) {
                        const probe = next[r]?.[colIndex(srcCol)]?.value
                        if (probe === '' || probe == null) continue
                        next[r][destCol] = { value: round2(built.formulaFor(r + 1)) }
                    }
                }
                return next
            })
            setSelectedColumn(null)
            setLibraryOpen(false)
            return
        }

        // ── Legacy path: user-saved string formula. ─────────────────────
        const formula = item.formula
        if (selectedColumn != null && selectedColumn >= 1) {
            const srcCol  = selectedColumn
            const destCol = srcCol + 1
            const srcColLetter = colLabel(srcCol)
            const isAggregate = /[A-Z]+\d+\s*:\s*[A-Z]+\d+/.test(formula)
            setGrid((g) => {
                const totalRows = g.length
                const next = g.map((row) => {
                    const r = row.slice()
                    // Insert at destCol, shifting any existing parameter
                    // column rightward rather than overwriting it.
                    while (r.length < destCol) r.push({ value: '' })
                    r.splice(destCol, 0, { value: '' })
                    return r
                })
                const stampHeader = (r, value) => {
                    if (!next[r]) next[r] = []
                    while (next[r].length <= destCol) next[r].push({ value: '' })
                    next[r][destCol] = { value, readOnly: true }
                }
                stampHeader(0, 'Calculated')
                stampHeader(1, item.name)
                stampHeader(2, item.parameterName || item.name)

                const retargeted = _retargetFormula(formula, srcColLetter)
                if (isAggregate) {
                    next[HEADER_ROWS][destCol] = { value: round2(retargeted) }
                } else {
                    for (let r = HEADER_ROWS; r < totalRows; r++) {
                        const srcVal = next[r]?.[srcCol]?.value
                        if (srcVal === '' || srcVal == null) continue
                        const adjusted = _replicateRowNumbers(retargeted, r + 1)
                        next[r][destCol] = { value: round2(adjusted) }
                    }
                }
                return next
            })
            setSelectedColumn(null)
            setLibraryOpen(false)
            return
        }

        if (activeReadOnly) { setLibraryOpen(false); return }
        setActiveCellValue(formula)
        setLibraryOpen(false)
    }
    const deleteUserFormula = (name) => {
        if (!confirm(`Remove saved formula "${name}" from your library?`)) return
        persistLibrary(userLibrary.filter((f) => f.name !== name))
        if (librarySel === name) setLibrarySel(serverLibrary[0]?.name || '')
    }

    // ── Save As Calculated Parameter ───────────────────────────────────────
    // Persists the active cell's formula to the local user library so it can
    // be re-applied from the Formula Library modal. Wiring this to a real
    // backend (CalculatedparameterViewSet) needs a schema change to accept a
    // free-form formula string and is out of scope for this UI pass.
    // Helper: turn a column letter ('C', 'AA') into a zero-based index.
    const _colIndex = (letter) => {
        const s = String(letter || '').toUpperCase()
        let n = 0
        for (let i = 0; i < s.length; i++) {
            const c = s.charCodeAt(i) - 64
            if (c < 1 || c > 26) return -1
            n = n * 26 + c
        }
        return n - 1
    }

    const openSaveAs = () => {
        // Two valid entry points:
        //   a) Active cell contains a formula → save THAT formula.
        //   b) A column is selected and Formula Library has a current pick →
        //      treat that picked formula as the one being saved.
        const hasActiveFormula = !activeReadOnly && String(activeCellValue).startsWith('=')
        const pickedLibraryItem = library.find((f) => f.name === librarySel)
        if (!hasActiveFormula && (selectedColumn == null || !pickedLibraryItem)) {
            alert('Select a cell with a formula (=…) — or pick a column and a Formula Library entry — before saving.')
            return
        }
        setSaveOutputParam(selectedParameters[0]?.name || '')
        setSaveRename('')
        // Default output column = the column right after the source.
        // (Active cell's column if we're saving a single-cell formula;
        // selectedColumn + 1 if we're saving a column-replicated formula.)
        const defaultCol = selectedColumn != null
            ? selectedColumn + 1
            : (active?.column ?? 1)
        setSaveOutputCol(colLabel(defaultCol))
        setSaveOpen(true)
    }
    const commitSaveAs = () => {
        const titleText = (saveRename || saveOutputParam || '').trim()
        if (!titleText) { alert('Pick an output parameter or enter a name.'); return }
        const destCol = _colIndex(saveOutputCol)
        if (destCol < 1) { alert('Output column must be a valid column letter (B or later — A is the Datetime gutter).'); return }

        const hasActiveFormula = !activeReadOnly && String(activeCellValue).startsWith('=')
        const formula = hasActiveFormula
            ? String(activeCellValue)
            : (library.find((f) => f.name === librarySel)?.formula || '')
        if (!formula) { alert('No formula to save.'); return }

        // Persist to the user library so it shows up in the Formula Library
        // dropdown next time.
        const next = [
            ...userLibrary.filter((f) => f.name !== titleText),
            {
                name:           titleText,
                formula,
                outputColumn:   saveOutputCol,
                parameterName:  saveOutputParam,
                savedAt:        new Date().toISOString(),
            },
        ]
        persistLibrary(next)

        // Apply the formula to the chosen output column AND stamp the title
        // text into row 0 (header) so the column shows the new name.
        const isAggregate = /[A-Z]+\d+\s*:\s*[A-Z]+\d+/.test(formula)
        const srcCol = selectedColumn != null ? selectedColumn : (active?.column ?? 1)
        const srcLetter = colLabel(srcCol)
        setGrid((g) => {
            const totalRows = g.length
            const expanded = g.map((row) => {
                const r = row.slice()
                // Insert a fresh cell at destCol, shifting existing
                // content (and any downstream parameter columns) one
                // position to the right. Avoids overwriting a sibling
                // parameter column with the new calculated column.
                while (r.length < destCol) r.push({ value: '' })
                r.splice(destCol, 0, { value: '' })
                return r
            })
            // Stamp the full 3-row header so the saved column reads as a
            // peer of source columns (Site / Device / Parameter+unit).
            const stampHeader = (r, value) => {
                if (!expanded[r]) expanded[r] = []
                while (expanded[r].length <= destCol) expanded[r].push({ value: '' })
                expanded[r][destCol] = { value, readOnly: true }
            }
            stampHeader(0, 'Calculated')
            stampHeader(1, titleText)
            stampHeader(2, saveOutputParam || titleText)

            const retargeted = _retargetFormula(formula, srcLetter)
            if (isAggregate) {
                expanded[HEADER_ROWS][destCol] = { value: round2(retargeted) }
            } else {
                for (let r = HEADER_ROWS; r < totalRows; r++) {
                    const srcVal = expanded[r]?.[srcCol]?.value
                    if (srcVal === '' || srcVal == null) continue
                    expanded[r][destCol] = { value: round2(_replicateRowNumbers(retargeted, r + 1)) }
                }
            }
            return expanded
        })

        // Action complete — drop the column pick.
        setSelectedColumn(null)
        setSaveOpen(false)
    }

    return (
        <div className="flex flex-col gap-2 flex-1 px-4 pb-4 min-h-0" style={{ fontFamily: "'Open Sans', sans-serif" }}>
            {/* Toolbar */}
            <div className="flex items-center justify-between bg-[#F1F2F4] rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                    {/* Zoom + undo/redo are desktop-only — the mobile toolbar keeps
                        just the fill/library/template actions so they fit. */}
                    <select value={zoom} onChange={(e) => setZoom(e.target.value)} className="hidden md:block bg-gray-50 border border-gray-200 px-2 py-1 rounded text-xs text-gray-700 hover:bg-gray-100 focus:outline-none">
                        {['50%', '75%', '100%', '125%', '150%'].map((z) => <option key={z} value={z}>{z}</option>)}
                    </select>
                    <button type="button" title="Undo" disabled className="hidden md:inline-flex p-1 text-gray-400 cursor-not-allowed rounded"><Undo2 className="w-6 h-6" /></button>
                    <button type="button" title="Redo" disabled className="hidden md:inline-flex p-1 text-gray-400 cursor-not-allowed rounded"><Redo2 className="w-6 h-6" /></button>
                    <span className="hidden md:inline-block mx-2 h-4 w-px bg-gray-300" aria-hidden="true" />
                    <button
                        type="button"
                        title="Fill Down (Ctrl+D) — copy the top cell of the selection through the rows below; relative refs shift by row"
                        onClick={fillDown}
                        className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                    >
                        <ArrowDownToLine className="w-6 h-6" />
                    </button>
                    <button
                        type="button"
                        title="Fill Right (Ctrl+R) — copy the left-most cell of the selection through the columns to the right; relative refs shift by column"
                        onClick={fillRight}
                        className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                    >
                        <ArrowRightToLine className="w-6 h-6" />
                    </button>
                    <span className="mx-2 h-4 w-px bg-gray-300" aria-hidden="true" />
                    <button
                        type="button"
                        title="Formula Library — pick a saved formula and insert it into the active cell"
                        onClick={openLibrary}
                        className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                    >
                        <Library className="w-6 h-6" />
                    </button>
                    <button
                        type="button"
                        title="Calculated Parameter Templates — Total Rain, Flow Accumulation, Offset, Rolling Average, Parameter Average, Math Formula"
                        onClick={openTemplates}
                        className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                    >
                        <Calculator className="w-6 h-6" />
                    </button>
                    <button
                        type="button"
                        title="Save As Calculated Parameter — save the active cell's formula to your library"
                        onClick={openSaveAs}
                        className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                    >
                        <FileDown className="w-6 h-6" />
                    </button>
                </div>
                {/* ⋮ overflow menu — desktop in the toolbar; on mobile portalled
                    into the tab-row group beside the calendar (matches the XD). */}
                <div className="hidden md:flex">{overflowMenu}</div>
            </div>

            {mobileMenuSlot && createPortal(<div className="md:hidden">{overflowMenu}</div>, mobileMenuSlot)}

            {/* Name box + formula bar (Excel layout). On a phone the controls wrap
                and the fx input drops to its own full-width line so nothing is
                squeezed; desktop stays a single row. */}
            <div className="flex flex-wrap md:flex-nowrap items-center gap-2 bg-[#F1F2F4] border border-gray-200 rounded-lg px-2 py-1">
                {/* Name box — current cell address, e.g. B5 */}
                <div className="min-w-[64px] px-2 py-1 border border-gray-200 rounded text-xs text-gray-700 bg-gray-50 text-center select-none" title="Active cell">
                    {cellName(active)}
                </div>
                <div className="relative">
                    {/* Excel-style function picker, grouped by category. Each
                        category mirrors Excel's "Insert Function" taxonomy
                        so the list reads naturally. Picking a name inserts
                        `=NAME()` into the active cell. */}
                    <select value={fn} onChange={(e) => insertFunction(e.target.value)} className="appearance-none sum-tab bg-white border border-gray-200 rounded text-xs px-2 pr-6 py-1 focus:outline-none focus:border-[#358ECD] cursor-pointer max-w-[160px]" title="Insert function">
                        {FUNCTION_CATEGORIES.map((g) => (
                            <optgroup key={g.label} label={g.label}>
                                {g.items.map((name) => <option key={name} value={name}>{name}</option>)}
                            </optgroup>
                        ))}
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-500 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                <div className="right-b">
                    <button type="button" onClick={cancelEdit} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Cancel — discard edits since last commit"><X className="w-6 h-6" /></button>
                    <button type="button" onClick={commitEdit} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Commit — snapshot current edits"><Check className="w-6 h-6" /></button>
                    <button type="button" onClick={() => insertFunction(fn)} className="px-1.5 py-0.5 text-xs italic font-serif text-gray-700 hover:bg-gray-100 rounded" title="Insert function">fx</button>
                </div>
                <div className="basis-full md:basis-auto flex-1 flex items-center gap-1 border border-gray-200 rounded-md px-2 py-1.5 bg-white md:bg-transparent">
                    <span className="common-size18 text-xs text-gray-500 select-none">fx</span>
                    <input
                        type="text"
                        value={barText}
                        onChange={(e) => onBarChange(e.target.value)}
                        disabled={activeReadOnly}
                        placeholder={placeholder}
                        className="flex-1 text-xs text-gray-800 bg-transparent focus:outline-none disabled:text-gray-400"
                    />
                </div>
            </div>

            {/* Spreadsheet */}
            <div ref={sheetRef} className="da-mobile-scroll bg-white border border-gray-200 overflow-auto max-h-[65vh] md:max-h-none md:flex-1 md:min-h-0">
                {!hasParams ? (
                    <div className="h-full flex items-center justify-center text-sm text-gray-400 p-8 text-center">Select one or more parameters to load data, then enter formulas in any empty cell.</div>
                ) : loading ? (
                    <div className="h-full flex items-center justify-center gap-2 text-sm text-gray-500"><Loader className="w-4 h-4 animate-spin" /> Loading data…</div>
                ) : (
                    <div style={{ zoom: zoomFactor }} className="dataanalytics-sheet">
                        <Spreadsheet
                            data={grid}
                            onChange={setGrid}
                            createFormulaParser={makeFormulaParser}
                            onActivate={(pt) => setActive(pt)}
                            onSelect={(selection) => {
                                // Capture the range (start + end points) so
                                // Fill Down / Fill Right can act on it even
                                // after focus leaves the grid.
                                if (selection?.range?.start && selection?.range?.end) {
                                    setRangeSel({ start: selection.range.start, end: selection.range.end })
                                }
                                // Detect whole-column selection. react-spreadsheet
                                // emits both EntireColumnsSelection (header click)
                                // and RangeSelection that happens to cover every
                                // data row of a single column. Treat either as
                                // "the user wants to operate on this column".
                                //
                                // IMPORTANT: we ONLY *update* selectedColumn when
                                // a whole-column selection is provided — we never
                                // clear it from inside onSelect. When the user
                                // clicks a Formula Library / Save As button, the
                                // spreadsheet emits an EmptySelection because focus
                                // moved away; clearing here would drop the column
                                // pick before they could even act on it. Selection
                                // is cleared explicitly after an apply finishes.
                                if (!selection) return
                                const cs = selection.columns ?? selection
                                if (cs?.start != null && cs?.end != null && typeof cs.start === 'number' && cs.start === cs.end) {
                                    setSelectedColumn(cs.start); return
                                }
                                const r = selection.range
                                if (r && r.start && r.end &&
                                    r.start.column === r.end.column &&
                                    r.start.row <= HEADER_ROWS && r.end.row >= grid.length - 1) {
                                    setSelectedColumn(r.start.column); return
                                }
                                // Anything else (single cell, multi-column range, etc.)
                                // — leave selectedColumn untouched so the modal flow
                                // still has the column the user picked earlier.
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Formula Library modal — matches the legacy WQDataLIVE design:
                "Select Formula" dropdown above, an Input section that
                renders the picked formula's typed inputs, and an Output
                section that names the resulting parameter. */}
            {libraryOpen && (
                <ModalShell title="Formula Library" onClose={() => setLibraryOpen(false)}>
                    <label className="common-size15 block text-xs font-bold text-[#4D4D4D] mb-1">Select Formula</label>
                    <div className="relative mb-3">
                        <select
                            value={librarySel}
                            onChange={(e) => {
                                const name = e.target.value
                                setLibrarySel(name)
                                setLibraryInputs(autoMapInputs(library.find((f) => f.name === name)))
                            }}
                            className="w-full appearance-none border border-gray-200 rounded px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:border-[#358ECD]"
                        >
                            <option value="">Select Formula</option>
                            {library.map((f) => (
                                <option key={f.name} value={f.name}>{f.name}</option>
                            ))}
                        </select>
                        <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>

                    {/* Per-formula Input form. Only shown once a template is
                        selected; user-saved formulas (legacy flat strings)
                        skip this block. */}
                    {currentLibTpl && Array.isArray(currentLibTpl.inputs) && (
                        <>
                            <div className="border-t border-gray-200 pt-3 mb-3" />
                            <h4 className="common-size15 font-bold text-[#4D4D4D] mb-2">Input</h4>

                            {/* Two-column grid so formulas with many inputs
                                (e.g. Water Level: trigger + Volume 1–9) stay
                                compact instead of one tall column. channelDims
                                spans both columns (it carries a diagram). */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                            {currentLibTpl.inputs.map((input) => {
                                // Shape-conditional inputs hide when the
                                // parent select doesn't match (currently
                                // unused — kept for future templates).
                                if (input.shapeOnly && libraryInputs.shape !== input.shapeOnly && (libraryInputs.shape || currentLibTpl.inputs.find((i) => i.key === 'shape')?.options?.[0]) !== input.shapeOnly) {
                                    return null
                                }

                                if (input.kind === 'singleParam') {
                                    // Backend defined this input against a specific
                                    // Parameterlib entry (via Unitlib). If exactly
                                    // one selected column matches, we've already
                                    // auto-bound it in autoMapInputs — surface a
                                    // read-only status line instead of a picker.
                                    const refId    = input.parameter_ref_id
                                    const boundPid = libraryInputs[input.key]
                                    const bound    = boundPid
                                        ? selectedParameters.find((sp) => String(sp.id) === String(boundPid))
                                        : null
                                    if (refId && bound) {
                                        return (
                                            <div key={input.key} className="mb-3">
                                                <div className="text-xs font-semibold text-gray-700 mb-1">{input.label}</div>
                                                <div className="flex items-center gap-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                                                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd"/></svg>
                                                    <span className="flex-1 truncate">{bound.name}{bound.unit ? ` (${bound.unit})` : ''}</span>
                                                    <span className="text-[10px] uppercase tracking-wide text-emerald-700">Auto-mapped</span>
                                                </div>
                                            </div>
                                        )
                                    }
                                    if (refId && !bound) {
                                        const refName = input.parameter_ref_name || input.label
                                        return (
                                            <div key={input.key} className="mb-3">
                                                <div className="text-xs font-semibold text-gray-700 mb-1">{input.label}</div>
                                                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                                    No selected column matches <strong>{refName}</strong>. Add a {refName} parameter from the sidebar, or pick one below.
                                                </div>
                                                <div className="relative mt-2">
                                                    <select
                                                        value={libraryInputs[input.key] || ''}
                                                        onChange={(e) => setLibraryInput(input.key, e.target.value)}
                                                        className="w-full appearance-none border border-gray-200 rounded px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:border-[#358ECD]"
                                                    >
                                                        <option value="">{`Select ${input.label}`}</option>
                                                        {selectedParameters.map((p) => (
                                                            <option key={p.id} value={p.id}>{p.name}{p.unit ? ` (${p.unit})` : ''}</option>
                                                        ))}
                                                    </select>
                                                    <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                                                </div>
                                            </div>
                                        )
                                    }
                                    return (
                                        <label key={input.key} className="block mb-3">
                                            <span className="common-size15 block text-xs font-bold text-gray-700 mb-1">{input.label}</span>
                                            <div className="relative">
                                                <select
                                                    value={libraryInputs[input.key] || ''}
                                                    onChange={(e) => setLibraryInput(input.key, e.target.value)}
                                                    className="w-full appearance-none border border-gray-200 rounded px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:border-[#358ECD]"
                                                >
                                                    <option value="">{`Select ${input.label}`}</option>
                                                    {selectedParameters.map((p) => (
                                                        <option key={p.id} value={p.id}>{p.name}{p.unit ? ` (${p.unit})` : ''}</option>
                                                    ))}
                                                </select>
                                                <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                                            </div>
                                        </label>
                                    )
                                }
                                // Numeric inputs ARE editable in the modal so the
                                // user can supply a value (e.g. Altitude (m) for
                                // Sea Level Barometric Pressure). Blank falls back
                                // to the backend default_value at compute time.
                                if (input.kind === 'number') {
                                    const val = libraryInputs[input.key]
                                    const def = (input.default_value ?? input.defaultValue)
                                    return (
                                        <label key={input.key} className="block mb-3">
                                            <span className="common-size15 block text-xs font-bold text-gray-700 mb-1">{input.label}</span>
                                            <input
                                                type="number"
                                                step="any"
                                                value={val ?? ''}
                                                placeholder={def != null && def !== '' ? `Default: ${def}` : 'Enter a value'}
                                                onChange={(e) => setLibraryInput(input.key, e.target.value)}
                                                className="w-full border border-gray-200 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#358ECD]"
                                            />
                                        </label>
                                    )
                                }
                                // `select` inputs stay backend-configured (their
                                // default_value is used at compute time) — e.g. the
                                // channel-shape selector that drives channelDims.
                                if (input.kind === 'select')  return null
                                if (input.kind === 'channelDims') {
                                    // Render the per-shape dimension inputs + a diagram of the
                                    // selected shape. The shape comes from a sibling `select`
                                    // input keyed 'shape'. If the user hasn't picked a shape
                                    // yet we fall back to the first option so the form isn't
                                    // empty.
                                    const shapeKey = libraryInputs.shape
                                        || currentLibTpl.inputs.find((i) => i.key === 'shape')?.options?.[0]
                                    const shapeCfg = CHANNEL_SHAPES[shapeKey]
                                    if (!shapeCfg) return null
                                    const dims = libraryInputs[input.key] || {}
                                    const setDim = (k, v) => setLibraryInput(input.key, { ...dims, [k]: v })
                                    return (
                                        <div key={input.key} className="mb-3 sm:col-span-2">
                                            <span className="common-size15 block text-xs font-bold text-gray-700 mb-1">{input.label}</span>
                                            <div className="grid grid-cols-[1fr_minmax(120px,140px)] gap-3 items-start">
                                                <div className="space-y-2">
                                                    {shapeCfg.dims.map((d) => (
                                                        <div key={d.key} className="flex items-center gap-2">
                                                            <span
                                                                className="w-6 text-xs font-semibold text-gray-700 text-right"
                                                                title={d.hint}
                                                            >
                                                                {d.label}
                                                            </span>
                                                            <input
                                                                type="number"
                                                                step="any"
                                                                value={dims[d.key] ?? ''}
                                                                onChange={(e) => setDim(d.key, e.target.value)}
                                                                placeholder={d.hint}
                                                                className="flex-1 min-w-0 border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#358ECD]"
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="border border-gray-200 rounded bg-gray-50 h-32 flex items-center justify-center p-1">
                                                    {shapeCfg.svg}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }
                                return null
                            })}
                            </div>

                            <div className="border-t border-gray-200 pt-3 mb-3" />
                            <h4 className="common-size15 text-sm font-bold text-[#4D4D4D] mb-1">Output</h4>
                            <p className="text-sm text-gray-700 mb-2">{currentLibTpl.output}</p>
                            {currentLibTpl.formulaType === 'lookup' && (
                                <div className="mb-2">
                                    <div className="text-[11px] font-semibold text-gray-700 mb-1">
                                        Lookup table — {(currentLibTpl.lookupData || []).length} entries
                                    </div>
                                    <div className="border border-gray-200 rounded bg-gray-50 p-2 text-[11px] font-mono max-h-32 overflow-auto">
                                        {(currentLibTpl.lookupData || []).slice(0, 8).map((row, i) => (
                                            <div key={i} className="text-gray-700">x={row.x} → y={row.y}</div>
                                        ))}
                                        {(currentLibTpl.lookupData || []).length > 8 && (
                                            <div className="text-gray-500 italic">…+{(currentLibTpl.lookupData || []).length - 8} more</div>
                                        )}
                                    </div>
                                    <div className="text-[10px] text-gray-500 mt-1">Linearly interpolates output between the two nearest table rows; clamps at the ends.</div>
                                </div>
                            )}
                            {(() => {
                                // Preview the final expression with substitutions
                                // — bound parameters render as "<Name>ᵣ",
                                // non-parameter inputs render as their backend
                                // default_value. Keeps math readable without
                                // exposing the placeholder syntax.
                                if (currentLibTpl.formulaType === 'lookup') return null
                                const raw = currentLibTpl.expression || ''
                                if (!raw) return null
                                let out = raw
                                const paramInputs = (currentLibTpl.inputs || []).filter(
                                    (i) => i.kind === 'singleParam' || i.kind === 'param',
                                )
                                const numericInputs = (currentLibTpl.inputs || []).filter(
                                    (i) => i.kind === 'number' || i.kind === 'select',
                                )
                                for (const p of paramInputs) {
                                    const pid   = libraryInputs[p.key]
                                    const bound = pid ? selectedParameters.find((sp) => String(sp.id) === String(pid)) : null
                                    const label = bound?.name || p.parameter_ref_name || p.label || p.key
                                    out = out.split(`{${p.key}}[r]`).join(`${label}ᵣ`)
                                    out = out.split(`{${p.key}}.col`).join(label)
                                }
                                for (const p of numericInputs) {
                                    let v = libraryInputs[p.key]
                                    if (v === undefined || v === null || v === '') v = p.defaultValue ?? p.default_value ?? 0
                                    out = out.split(`{${p.key}}`).join(String(v))
                                }
                                return (
                                    // Raw expression is useful on desktop but noisy on a phone —
                                    // the mobile XD omits it, so it's desktop-only.
                                    <pre className="hidden md:block text-[12px] font-mono text-gray-800 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 mb-2 whitespace-pre-wrap break-all">{out}</pre>
                                )
                            })()}
                            {currentLibTpl.note && (
                                <p className="text-[11px] text-gray-600 mb-3">{currentLibTpl.note}</p>
                            )}
                        </>
                    )}

                    {/* User-saved formulas (legacy flat strings) listed below
                        so the user can quickly remove old entries. */}
                    {userLibrary.length > 0 && !currentLibTpl?.inputs && (
                        <div className="mb-3 max-h-32 overflow-auto text-xs">
                            <div className="font-semibold text-gray-600 mb-1">Your saved formulas:</div>
                            <ul>
                                {userLibrary.map((f) => (
                                    <li key={f.name} className="flex items-center justify-between py-0.5">
                                        <span className="truncate text-gray-700">{f.name} <span className="text-gray-400">{f.formula}</span></span>
                                        <button type="button" onClick={() => deleteUserFormula(f.name)} className="text-red-500 hover:underline ml-2">Remove</button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={applyLibrarySelection}
                        disabled={!librarySel}
                        className="common-size15 w-full bg-[#358ECD] hover:bg-[#2A78B0] disabled:bg-gray-300 text-white text-sm font-bold rounded py-2 mb-2"
                    >
                        SELECT FORMULA
                    </button>
                    <button type="button" onClick={() => setLibraryOpen(false)} className="common-size15 block mx-auto text-xs font-semibold text-[#358ECD] hover:underline">Cancel</button>
                </ModalShell>
            )}

            {/* Calculated Parameter Templates modal */}
            {templateOpen && (
                <ModalShell title="Calculated Parameter Templates" onClose={() => setTemplateOpen(false)}>
                    <label className="common-size15 block text-xs font-bold text-[#4D4D4D] mb-1">Formula</label>
                    <div className="relative mb-3">
                        <select
                            value={templateKey}
                            onChange={(e) => { setTemplateKey(e.target.value); setTemplateInputs({}) }}
                            className="w-full appearance-none border border-gray-200 rounded px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:border-[#358ECD]"
                        >
                            {CALC_TEMPLATES.map((t) => (
                                <option key={t.key} value={t.key}>{t.name}</option>
                            ))}
                        </select>
                        <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                    {currentTemplate.note && (
                        <p className="text-[11px] text-gray-500 mb-3 italic">{currentTemplate.note}</p>
                    )}

                    {/* Per-template input form */}
                    {currentTemplate.inputs.map((input) => {
                        if (input.kind === 'singleParam') {
                            return (
                                <label key={input.key} className="block mb-3">
                                    <span className="common-size15 block text-xs font-bold text-[#4D4D4D] mb-1">{input.label}</span>
                                    <div className="relative">
                                        <select
                                            value={templateInputs[input.key] || ''}
                                            onChange={(e) => setTplInput(input.key, e.target.value)}
                                            className="w-full appearance-none border border-gray-200 rounded px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:border-[#358ECD]"
                                        >
                                            <option value="">— pick a parameter —</option>
                                            {selectedParameters.map((p) => (
                                                <option key={p.id} value={p.id}>{p.name}{p.unit ? ` (${p.unit})` : ''}</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                                    </div>
                                </label>
                            )
                        }
                        if (input.kind === 'multiParam') {
                            const selectedIds = templateInputs[input.key] || []
                            const toggle = (id) => {
                                const s = new Set(selectedIds.map(String))
                                if (s.has(String(id))) s.delete(String(id)); else s.add(String(id))
                                setTplInput(input.key, Array.from(s))
                            }
                            return (
                                <div key={input.key} className="mb-3">
                                    <span className="common-size15 block text-xs font-bold text-[#4D4D4D] mb-1">
                                        {input.label} <span className="font-normal text-gray-400">(pick {input.min || 1}+)</span>
                                    </span>
                                    <div className="max-h-32 overflow-y-auto border border-gray-200 rounded">
                                        {selectedParameters.length === 0 ? (
                                            <p className="p-2 text-xs text-gray-400 italic">No parameters loaded yet — select some on the left sidebar.</p>
                                        ) : selectedParameters.map((p, i) => (
                                            <label key={p.id} className="flex items-center gap-2 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.map(String).includes(String(p.id))}
                                                    onChange={() => toggle(p.id)}
                                                />
                                                <span className="font-mono text-gray-500 w-6">P{i + 1}</span>
                                                <span className="flex-1">{p.name}{p.unit ? ` (${p.unit})` : ''}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )
                        }
                        if (input.kind === 'number') {
                            return (
                                <label key={input.key} className="block mb-3">
                                    <span className="common-size15 block text-xs font-bold text-[#4D4D4D] mb-1">{input.label}</span>
                                    <input
                                        type="number"
                                        step={input.step ?? 'any'}
                                        min={input.min}
                                        value={templateInputs[input.key] ?? input.defaultValue ?? ''}
                                        onChange={(e) => setTplInput(input.key, e.target.value)}
                                        className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
                                    />
                                </label>
                            )
                        }
                        if (input.kind === 'text') {
                            return (
                                <label key={input.key} className="block mb-3">
                                    <span className="common-size15 block text-xs font-bold text-[#4D4D4D] mb-1">{input.label}</span>
                                    <input
                                        type="text"
                                        value={templateInputs[input.key] ?? input.defaultValue ?? ''}
                                        onChange={(e) => setTplInput(input.key, e.target.value)}
                                        placeholder={input.placeholder}
                                        className="w-full border border-gray-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#358ECD]"
                                    />
                                </label>
                            )
                        }
                        return null
                    })}

                    <button type="button" onClick={applyTemplate} className="common-size15 w-full bg-[#358ECD] hover:bg-[#2A78B0] text-white text-sm font-semibold rounded py-2 mb-2">
                        APPLY
                    </button>
                    <button type="button" onClick={() => setTemplateOpen(false)} className="common-size15 block mx-auto font-semibold text-xs text-[#358ECD] hover:underline">Cancel</button>
                </ModalShell>
            )}

            {/* Save As Calculated Parameter modal */}
            {saveOpen && (
                <ModalShell title="Save As Calculated Parameter" onClose={() => setSaveOpen(false)}>
                    <label className="common-size15 block text-xs font-bold text-[#4D4D4D] mb-1">Select Output Parameter</label>
                    <div className="relative mb-3">
                        <select
                            value={saveOutputParam}
                            onChange={(e) => setSaveOutputParam(e.target.value)}
                            className="w-full appearance-none border border-gray-200 rounded px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:border-[#358ECD]"
                        >
                            <option value="">— pick a parameter —</option>
                            {selectedParameters.map((p) => (
                                <option key={p.id} value={p.name}>{p.name}{p.unit ? ` (${p.unit})` : ''}</option>
                            ))}
                        </select>
                        <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>

                    <label className="common-size15 common-size15 block text-xs font-bold text-[#4D4D4D] mb-1">Rename (Optional)</label>
                    <input
                        type="text"
                        value={saveRename}
                        onChange={(e) => setSaveRename(e.target.value)}
                        placeholder="Name"
                        className="w-full border border-gray-200 rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:border-[#358ECD]"
                    />

                    <label className="common-size15 block text-xs font-bold text-[#4D4D4D] mb-1">Output Column</label>
                    <input
                        type="text"
                        value={saveOutputCol}
                        onChange={(e) => setSaveOutputCol(e.target.value.toUpperCase())}
                        className="w-full border border-gray-200 rounded px-3 py-2 text-sm mb-4 focus:outline-none focus:border-[#358ECD]"
                    />

                    <button type="button" onClick={commitSaveAs} className="common-size15 w-full bg-[#358ECD] hover:bg-[#2A78B0] text-white text-sm font-semibold rounded py-2 mb-2">SAVE</button>
                    <button type="button" onClick={() => setSaveOpen(false)} className="common-size15 block mx-auto font-semibold text-xs text-[#358ECD] hover:underline">Cancel</button>
                    <div className="mt-3 text-[11px] text-gray-500 text-center">Formula saved: <code className="text-gray-700">{String(activeCellValue)}</code></div>
                </ModalShell>
            )}
        </div>
    )
}

function ModalShell({ title, onClose, children }) {
    // Scrollable-overlay pattern: the OVERLAY scrolls (not an inner max-h box), so
    // a tall form (e.g. Manning Equation) is fully reachable on a phone — the old
    // centered `max-h-[90vh]` box pushed the title and the bottom Cancel outside
    // the visible viewport (mobile `vh` includes the browser toolbar) and they
    // couldn't be scrolled to. Portalled to <body> so `fixed inset-0` is always
    // viewport-relative and the dim covers the app header too.
    return createPortal(
        <div
            className="fixed inset-0 z-[10000] overflow-y-auto bg-black/40"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    className="bg-white rounded-lg shadow-xl w-full max-w-xl p-5 my-4"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="common-head25 text-base font-bold text-[#4D4D4D]">{title}</h3>
                        <button type="button" onClick={onClose} className="p-1 text-gray-500 hover:bg-gray-100 rounded" aria-label="Close">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    {children}
                </div>
            </div>
        </div>,
        document.body,
    )
}
